import React from 'react';
import LegalLayout from '../components/LegalLayout';
import SEO from '../components/SEO';

const Terms: React.FC = () => {
    return (
        <LegalLayout>
            <SEO
                title="Terms of Service"
                description="The terms and conditions for using Share Visuals."
            />
            <div className="animate-fade-in">
                {/* Header */}
                <div className="text-center mb-16 pb-12 border-b border-white/5 group">
                    <div className="inline-block bg-secondary-500/10 border border-secondary-500/20 text-secondary-400 text-[10px] font-black uppercase tracking-[0.25em] px-5 py-2 rounded-full mb-8 group-hover:bg-secondary-500/15 transition-colors">
                        Terms & Conditions
                    </div>
                    <h1 className="text-4xl md:text-7xl font-black text-white mb-6 tracking-tight leading-tight">Terms of Service</h1>
                    <p className="text-dark-500 font-bold uppercase tracking-widest text-xs">Last updated: <span className="text-secondary-400">February 26, 2026</span></p>
                </div>

                {/* Intro Box */}
                <div className="relative mb-20">
                    <div className="absolute -inset-1 bg-gradient-to-r from-secondary-500/20 to-primary-500/20 rounded-3xl blur-md opacity-50" />
                    <div className="relative bg-[#0c1120] border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-2xl bg-secondary-500/20 flex items-center justify-center shrink-0 text-xl">📋</div>
                            <div className="text-dark-200 leading-relaxed text-lg font-medium italic">
                                <strong className="text-secondary-400 not-italic block mb-1 uppercase text-xs tracking-widest">The Short Version</strong>
                                Use Share Visuals for legal purposes. Content you create belongs to you. We offer a 7-day money-back guarantee. We can suspend accounts that violate these terms. By using Share Visuals, you agree to these terms.
                            </div>
                        </div>
                    </div>
                </div>

                {/* TOC */}
                <div className="bg-white/[0.01] border border-white/5 rounded-3xl p-8 md:p-10 mb-24">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-dark-500 mb-8 flex items-center gap-3">
                        <span className="w-8 h-px bg-white/10" />
                        Table of Contents
                    </h4>
                    <ol className="grid md:grid-cols-2 gap-y-4 gap-x-12 list-decimal list-inside text-sm font-bold text-dark-400">
                        {[
                            'Acceptance of Terms',
                            'Description of Service',
                            'Account Registration',
                            'Subscription Plans & Billing',
                            'Refund Policy',
                            'Acceptable Use',
                            'Content & Intellectual Property',
                            'Free Plan Limitations',
                            'Service Availability',
                            'Limitation of Liability',
                            'Account Termination',
                            'Changes to Terms',
                        ].map((item, i) => (
                            <li key={i} className="hover:text-white transition-colors cursor-default border-b border-transparent hover:border-secondary-500/30 pb-1">
                                <span className="text-dark-300 ml-2">{item}</span>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* Main Sections */}
                <div className="space-y-24">

                    {/* 1. Acceptance */}
                    <section>
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">1</span>
                            Acceptance of Terms
                        </h2>
                        <p className="text-dark-300 leading-relaxed font-medium">
                            By accessing or using Share Visuals at <strong className="text-white">sharevisuals.com</strong>, you confirm that you are at least 13 years of age, have the legal capacity to enter this agreement, and agree to be bound by these Terms. If you disagree, please do not use our service.
                        </p>
                    </section>

                    {/* 2. Service */}
                    <section>
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">2</span>
                            Description of Service
                        </h2>
                        <div className="grid md:grid-cols-3 gap-4">
                            {[
                                { icon: '📝', title: 'Quote Art', desc: 'Create branded quote cards for X, Instagram, and LinkedIn.' },
                                { icon: '📊', title: 'Stats Cards', desc: 'Visualize your metrics and SaaS progress in seconds.' },
                                { icon: '🚀', title: 'SaaS Updates', desc: 'Build-in-public update cards with progress bars.' },
                            ].map((item, i) => (
                                <div key={i} className="bg-white/[0.02] border border-white/5 hover:border-white/10 p-6 rounded-3xl transition-all hover:bg-white/[0.04] group cursor-default">
                                    <div className="text-2xl mb-3 group-hover:scale-110 transition-transform">{item.icon}</div>
                                    <h4 className="text-white font-bold mb-2 text-sm">{item.title}</h4>
                                    <p className="text-dark-500 text-xs leading-relaxed font-medium">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* 3. Account */}
                    <section>
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">3</span>
                            Account Registration
                        </h2>
                        <p className="text-dark-300 leading-relaxed mb-6 font-medium">You are responsible for all activity under your account. Provide accurate information, use a strong password, and notify us immediately of any unauthorized access.</p>
                        <div className="bg-orange-500/5 border border-orange-500/15 rounded-2xl p-5">
                            <p className="text-orange-300 text-sm font-medium">⚠️ Each account is for individual use only. Sharing credentials to bypass plan limits will result in account termination.</p>
                        </div>
                    </section>

                    {/* 4. Billing */}
                    <section className="bg-gradient-to-br from-[#1E1B4B]/30 to-transparent border border-white/5 rounded-[40px] p-8 md:p-12">
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">4</span>
                            Subscription Plans & Billing
                        </h2>

                        <div className="grid md:grid-cols-2 gap-8 mb-8">
                            {[
                                { plan: 'Free Plan', price: '$0', perks: ['10 visuals/day', 'Watermarked images', 'Basic templates'] },
                                { plan: 'Creator', price: '$5/mo', perks: ['Unlimited visuals', 'No watermark', 'All templates'] },
                                { plan: 'Pro', price: '$15/mo', perks: ['Everything in Creator', 'Batch CSV generation', 'ZIP download'] },
                                { plan: 'Lifetime', price: '$39 once', perks: ['All Pro features forever', 'Priority support', 'Future templates included'] },
                            ].map((tier, i) => (
                                <div key={i} className="bg-white/[0.03] border border-white/5 rounded-2xl p-5 hover:border-primary-500/20 transition-colors">
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="text-white font-black text-sm">{tier.plan}</h4>
                                        <span className="text-primary-400 font-black text-sm bg-primary-500/10 px-3 py-1 rounded-full">{tier.price}</span>
                                    </div>
                                    <ul className="space-y-1.5">
                                        {tier.perks.map((perk, j) => (
                                            <li key={j} className="text-dark-400 text-xs font-medium flex items-center gap-2">
                                                <svg className="w-3 h-3 text-primary-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                {perk}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>

                        <div className="bg-secondary-500/5 border border-secondary-500/10 rounded-xl p-5">
                            <p className="text-secondary-300 text-sm font-medium">💳 Payments are processed by <strong>DodoPayments</strong> with bank-level encryption. We never store your card details.</p>
                        </div>
                    </section>

                    {/* 5. Refund */}
                    <section>
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">5</span>
                            Refund Policy
                        </h2>

                        <div className="relative overflow-hidden bg-gradient-to-br from-green-500/10 to-transparent border border-green-500/20 rounded-3xl p-8 mb-8 text-center">
                            <div className="text-4xl mb-4">✅</div>
                            <h3 className="text-2xl font-black text-white mb-2">7-Day Money Back Guarantee</h3>
                            <p className="text-dark-300 font-medium max-w-sm mx-auto">Not happy? Get a full refund within 7 days of your first payment — no questions asked.</p>
                        </div>

                        <div className="space-y-4 text-dark-400 text-sm font-medium">
                            <p>• Refund requests must be submitted within 7 days of initial payment.</p>
                            <p>• Lifetime plan refunds follow the same 7-day policy.</p>
                            <p>• After 7 days, refunds are evaluated case-by-case — we always aim to make it right.</p>
                            <p>• To request: email <a href="mailto:hello@sharevisuals.com" className="text-primary-400 hover:text-primary-300 underline">hello@sharevisuals.com</a> with your account email and order ID.</p>
                        </div>
                    </section>

                    {/* 6. Acceptable Use */}
                    <section>
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">6</span>
                            Acceptable Use
                        </h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-red-500/5 border border-red-500/15 rounded-2xl p-6">
                                <h4 className="text-red-400 font-black text-sm mb-4 uppercase tracking-widest">Prohibited Content</h4>
                                <ul className="space-y-2 text-dark-400 text-xs font-medium">
                                    {['Illegal, defamatory, or fraudulent content', 'Content infringing intellectual property rights', 'Hate speech, violence, or adult content', 'Spam or deliberate deceptive content', 'Impersonation of others'].map((item, i) => (
                                        <li key={i} className="flex items-start gap-2"><span className="text-red-500 mt-0.5 flex-shrink-0">✕</span>{item}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-red-500/5 border border-red-500/15 rounded-2xl p-6">
                                <h4 className="text-red-400 font-black text-sm mb-4 uppercase tracking-widest">Prohibited Actions</h4>
                                <ul className="space-y-2 text-dark-400 text-xs font-medium">
                                    {['Reverse engineering or hacking the platform', 'Automated scripts abusing API limits', 'Circumventing plan restrictions', 'Sharing accounts to bypass limits', 'Accessing other users\' data'].map((item, i) => (
                                        <li key={i} className="flex items-start gap-2"><span className="text-red-500 mt-0.5 flex-shrink-0">✕</span>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* 7. IP */}
                    <section>
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">7</span>
                            Content & Intellectual Property
                        </h2>

                        <div className="space-y-6">
                            <div className="flex gap-6 items-start">
                                <div className="flex-1 bg-white/[0.02] border border-white/5 p-6 rounded-2xl">
                                    <h4 className="text-white font-bold text-sm mb-2 flex items-center gap-2">🎨 Your Content</h4>
                                    <p className="text-dark-400 text-sm leading-relaxed font-medium">Everything you create using Share Visuals belongs to you. You retain full copyright of the quotes, visuals, and designs you produce. All plans allow commercial use of your generated visuals.</p>
                                </div>
                                <div className="flex-1 bg-white/[0.02] border border-white/5 p-6 rounded-2xl">
                                    <h4 className="text-white font-bold text-sm mb-2 flex items-center gap-2">🏢 Our Platform</h4>
                                    <p className="text-dark-400 text-sm leading-relaxed font-medium">The Share Visuals platform, brand, templates, design, and code are owned by Share Visuals. You may not copy, sell, or create derivative products based on our platform without permission.</p>
                                </div>
                            </div>

                            <div className="bg-primary-500/5 border border-primary-500/10 rounded-2xl p-5 text-center">
                                <p className="text-primary-300 text-sm font-medium">⭐ Free plan images include a "Share Visuals" watermark. <strong className="text-white">All paid plans remove watermarks permanently.</strong></p>
                            </div>
                        </div>
                    </section>

                    {/* 8 – 12 Rich Cards */}
                    <div className="space-y-8">
                        {[
                            {
                                num: '8',
                                icon: '🆓',
                                title: 'Free Plan Limitations',
                                accent: 'from-amber-500/10 to-transparent border-amber-500/15',
                                badge: 'text-amber-400 bg-amber-500/10',
                                bullets: [
                                    'Max 10 visuals created per day (resets midnight UTC)',
                                    '"Share Visuals" watermark on all generated images',
                                    'Access to basic templates only',
                                    'Standard email support response time',
                                    'No batch CSV generation or ZIP download',
                                    'Free plan limits may change with 14 days\' notice',
                                ]
                            },
                            {
                                num: '9',
                                icon: '⚡',
                                title: 'Service Availability',
                                accent: 'from-secondary-500/10 to-transparent border-secondary-500/15',
                                badge: 'text-secondary-400 bg-secondary-500/10',
                                bullets: [
                                    'We target 99.9% uptime — monitored continuously',
                                    'Planned maintenance is announced at least 24h in advance',
                                    'Emergency fixes or third-party outages can cause brief downtime',
                                    'We are not liable for any losses caused by service unavailability',
                                    'Paid plan users experiencing extended downtime may request account credit',
                                ]
                            },
                            {
                                num: '10',
                                icon: '⚖️',
                                title: 'Limitation of Liability',
                                accent: 'from-purple-500/10 to-transparent border-purple-500/15',
                                badge: 'text-purple-400 bg-purple-500/10',
                                bullets: [
                                    'Share Visuals is not liable for indirect or consequential damages',
                                    'Not liable for loss of profits, data, or business opportunities',
                                    'Maximum liability capped at fees paid in the last 3 months',
                                    'Service is provided "as is" — without warranty of any kind',
                                ]
                            },
                            {
                                num: '11',
                                icon: '🔑',
                                title: 'Account Termination',
                                accent: 'from-red-500/10 to-transparent border-red-500/15',
                                badge: 'text-red-400 bg-red-500/10',
                                bullets: [
                                    'You may cancel your subscription anytime from Account Settings',
                                    'Access continues until the end of your current billing period',
                                    'Account data is purged within 30 days of permanent deletion',
                                    'We may suspend accounts that violate these Terms or engage in fraud',
                                    'Serious violations may result in immediate suspension without refund',
                                ]
                            },
                            {
                                num: '12',
                                icon: '📄',
                                title: 'Changes to Terms',
                                accent: 'from-primary-500/10 to-transparent border-primary-500/15',
                                badge: 'text-primary-400 bg-primary-500/10',
                                bullets: [
                                    'We may update these Terms at any time',
                                    'Significant changes notified via email at least 14 days in advance',
                                    'A notice will also appear on the Share Visuals dashboard',
                                    'Continued use after the effective date = acceptance of new Terms',
                                ]
                            },
                        ].map((section) => (
                            <div key={section.num} className={`relative bg-gradient-to-br ${section.accent} border rounded-3xl p-8 md:p-10 group hover:scale-[1.01] transition-all duration-300`}>
                                <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                                    {/* Left: Number + Icon */}
                                    <div className="flex items-center gap-4 sm:flex-col sm:items-center sm:gap-2 sm:w-16 flex-shrink-0">
                                        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20 group-hover:scale-110 transition-transform">
                                            {section.num}
                                        </div>
                                        <div className="text-2xl sm:mt-1">{section.icon}</div>
                                    </div>

                                    {/* Right: Content */}
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-2xl font-black text-white mb-5 tracking-tight">{section.title}</h2>
                                        <ul className="space-y-3">
                                            {section.bullets.map((bullet, i) => (
                                                <li key={i} className="flex items-start gap-3 text-dark-300 text-sm font-medium leading-relaxed">
                                                    <span className={`shrink-0 mt-0.5 text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${section.badge}`}>✦</span>
                                                    {bullet}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </LegalLayout>
    );
};

export default Terms;
